import React, { useState, useEffect } from 'react';
import Input from '../../../Components/Input/Input';
import Button from '../../../Components/Button/Button';
import { getApi, patchApi } from '../../../Services/apiService';
import { toast } from 'react-toastify';

function mapProfileToForm(adminData) {
  if (!adminData) return null;

  return {
    id: adminData?.id || '',
    name: adminData?.name || '',
    email: adminData?.email || '',
    profileImage: adminData?.profile_image || adminData?.image || null,
  };
}

function getNameInitials(name) {
  if (!name?.trim()) return '';
  return name
    .trim()
    .split(/\s+/)
    .map((part) => part[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
}

function getAdminProfileFromStorage() {
  try {
    const adminDataString = localStorage.getItem('adminData');
    if (!adminDataString) return null;
    return mapProfileToForm(JSON.parse(adminDataString));
  } catch {
    return null;
  }
}

function syncAdminDataToStorage(adminData) {
  if (!adminData || typeof adminData !== 'object') return;
  localStorage.setItem('adminData', JSON.stringify(adminData));
  window.dispatchEvent(new CustomEvent('adminDataUpdated'));
}

export default function Profile() {
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isFetchingProfile, setIsFetchingProfile] = useState(true);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
  });
  const [originalData, setOriginalData] = useState({ ...formData });
  const [profileImage, setProfileImage] = useState(null);
  const [profileImagePreview, setProfileImagePreview] = useState(null);
  const [originalImage, setOriginalImage] = useState(null);
  const fileInputRef = React.useRef(null);

  const applyProfileData = (data) => {
    if (!data) return;

    const initialData = {
      name: data.name,
      email: data.email,
    };

    setFormData(initialData);
    setOriginalData(initialData);

    if (data.profileImage) {
      setProfileImagePreview(data.profileImage);
      setOriginalImage(data.profileImage);
    } else {
      setProfileImagePreview(null);
      setOriginalImage(null);
    }
  };

  useEffect(() => {
    let cancelled = false;

    const fetchProfile = async () => {
      setIsFetchingProfile(true);

      try {
        const response = await getApi('/admin/profile');

        if (cancelled) return;

        if (response?.statusCode === 200 && response?.data) {
          syncAdminDataToStorage(response.data);
          applyProfileData(mapProfileToForm(response.data));
          return;
        }

        applyProfileData(getAdminProfileFromStorage());
        if (response?.message) {
          toast.error(response.message);
        }
      } catch (error) {
        if (cancelled) return;
        applyProfileData(getAdminProfileFromStorage());
        const message =
          error?.response?.data?.message || error?.message || 'Failed to load profile';
        toast.error(message);
      } finally {
        if (!cancelled) {
          setIsFetchingProfile(false);
        }
      }
    };

    fetchProfile();

    return () => {
      cancelled = true;
    };
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleEdit = () => {
    setOriginalData({ ...formData });
    setOriginalImage(profileImagePreview);
    setIsEditing(true);
  };

  const handleCancel = () => {
    setFormData({ ...originalData });
    setProfileImage(null);
    setProfileImagePreview(originalImage);
    setIsEditing(false);
  };

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      toast.error('Please upload a valid image file (jpeg, jpg, png, gif, webp)');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image size should be less than 5MB');
      return;
    }

    setProfileImage(file);

    const reader = new FileReader();
    reader.onloadend = () => {
      setProfileImagePreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const handleCameraClick = () => {
    fileInputRef.current?.click();
  };

  const handleSave = async () => {
    if (!formData.name?.trim()) {
      toast.error('Name is required');
      return;
    }

    setIsLoading(true);

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('name', formData.name.trim());

      if (profileImage) {
        formDataToSend.append('image', profileImage);
      }

      const response = await patchApi('/admin/profile', formDataToSend, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response?.statusCode === 200 || response?.statusCode === 201) {
        toast.success(response?.message || 'Profile updated successfully');

        const profileResponse = await getApi('/admin/profile');
        if (profileResponse?.statusCode === 200 && profileResponse?.data) {
          syncAdminDataToStorage(profileResponse.data);
          applyProfileData(mapProfileToForm(profileResponse.data));
        } else if (response?.data) {
          syncAdminDataToStorage(response.data);
          applyProfileData(mapProfileToForm(response.data));
        }

        setProfileImage(null);
        setIsEditing(false);
      } else {
        toast.error(response?.message || 'Failed to update profile. Please try again.');
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to update profile. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="settings-profile-card">
      {isFetchingProfile ? (
        <p className="admin-profile-loading">Loading profile...</p>
      ) : (
        <>
          <div className="profile-header">
            <div className="profile-avatar-section">
              <div className="profile-avatar-container">
                <div className="profile-avatar">
                  {profileImagePreview ? (
                    <img src={profileImagePreview} alt="Profile" className="profile-avatar-image" />
                  ) : (
                    <div className="profile-avatar-placeholder">{getNameInitials(formData.name)}</div>
                  )}
                  {isEditing && (
                    <div className="profile-avatar-camera-overlay" onClick={handleCameraClick}>
                      <svg
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        className="profile-camera-icon"
                      >
                        <path
                          d="M23 19C23 19.5304 22.7893 20.0391 22.4142 20.4142C22.0391 20.7893 21.5304 21 21 21H3C2.46957 21 1.96086 20.7893 1.58579 20.4142C1.21071 20.0391 1 19.5304 1 19V8C1 7.46957 1.21071 6.96086 1.58579 6.58579C1.96086 6.21071 2.46957 6 3 6H7L9 4H15L17 6H21C21.5304 6 22.0391 6.21071 22.4142 6.58579C22.7893 6.96086 23 7.46957 23 8V19Z"
                          stroke="white"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <circle cx="12" cy="13" r="4" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </div>
                  )}
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                  onChange={handleImageChange}
                  style={{ display: 'none' }}
                />
              </div>
            </div>
            <div className="profile-actions">
              {!isEditing ? (
                <button className="profile-edit-button" onClick={handleEdit}>
                  <img
                    src={window.getPublicUrl ? window.getPublicUrl('/assets/images/EditIcon.svg') : '/assets/images/EditIcon.svg'}
                    alt="Edit"
                    className="profile-edit-icon"
                  />
                </button>
              ) : (
                <div className="profile-action-buttons">
                  <Button className="btn-secondary profile-cancel-button" onClick={handleCancel}>
                    Cancel
                  </Button>
                  <Button className="btn-success profile-save-button" onClick={handleSave} disabled={isLoading}>
                    {isLoading ? 'Saving...' : 'Save'}
                  </Button>
                </div>
              )}
            </div>
          </div>

          <div className="profile-form">
            <div className="profile-form-row">
              <div className="profile-form-column">
                <Input
                  label="Name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  type="text"
                  disabled={!isEditing}
                  readOnly={!isEditing}
                />
              </div>
              <div className="profile-form-column">
                <Input
                  label="Email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  type="email"
                  disabled
                  readOnly
                />
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
